import { intervalToDuration, formatDuration, differenceInMinutes } from 'date-fns';
import { BookingRequestExtra, ProviderBookingRead, StatusByProviderEnum } from '../../../api';
import { ExtraIncome } from './ExtraIncomeProvider';

export const TIME_FORMAT = 'HH:mm';
export const TimeMask = '00:00';

export const requestBodyFields = {
  spentMinutes: 'extra_hours_time_spent_minutes',
  totalAmount: 'extra_hours_total_amount',
  additionalBonus: 'additional_charges_bonus',
  travelExpenses: 'additional_charges_travel_expenses',
};

const getTimeDuration = (minutes: number) => {
  return intervalToDuration({ start: 0, end: minutes * 1000 * 60 });
};

const zeroPad = (num: number) => String(num).padStart(2, '0');

export const getFormattedTime = (minutes: number): string => {
  const duration = getTimeDuration(minutes);
  return formatDuration(duration, {
    format: ['hours', 'minutes'],
    zero: true,
    delimiter: ':',
    locale: {
      formatDistance: (_token, count) => zeroPad(count),
    },
  });
};

export const getMinutesFromTime = (time: string | undefined): number => {
  if (!time) return 0;
  const [hours, minutes, seconds] = time.split(':');
  const date = new Date();
  date.setHours(+hours || 0, +minutes || 0, +seconds || 0, 0);
  return date.getHours() * 60 + date.getMinutes();
};

export const requestBodyFieldsFormatHandlers = {
  spentMinutes: (v: string) => getMinutesFromTime(v),
  totalAmount: (v: number) => String(v),
  additionalBonus: (v: number) => String(v),
  travelExpenses: (v: number) => String(v),
};

export const prepareRequestBody = (data: ExtraIncome, currentExtra: ExtraIncome) => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    const fieldName = requestBodyFields[key];
    if (currentExtra[key] !== data[key]) {
      acc[fieldName] = requestBodyFieldsFormatHandlers[key](value || 0);
    }
    return acc;
  }, {} as BookingRequestExtra);
};

function roundToQuarterHour(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

export const getBookingDuration = (booking: ProviderBookingRead, rounded = true) => {
  const { start_on, start_time, end_on, end_time } = booking;
  const start = new Date(`${start_on}T${start_time}.000Z`);
  const end = new Date(`${end_on}T${end_time}.000Z`);
  const duration = differenceInMinutes(end, start);
  return rounded ? roundToQuarterHour(duration) : duration;
};

const normalizeInHours = (
  duration: Duration,
  key: string = 'days',
  normalizedKey: string = 'hours',
  normalizedValue: number = 24
) => {
  return Object.entries(duration).reduce((acc, [period, value]) => {
    if (period === key) {
      acc[normalizedKey] = value * normalizedValue;
      return acc;
    }
    if (period === normalizedKey) {
      acc[normalizedKey] += value;
      return acc;
    }
    if (value) {
      acc[period] = value;
    }
    return acc;
  }, {} as Duration);
};

export const formDurationOutput = (duration: Duration) => {
  return formatDuration(duration, {
    format: ['days', 'hours', 'minutes'],
    zero: true,
    locale: {
      formatDistance: (_token, count) => {
        switch (_token) {
          case 'xMinutes':
            return `${count}m`;
          case 'xHours':
            return `${count}h`;
          default:
            return count;
        }
      },
    },
  });
};

export const getBookingTime = (booking: ProviderBookingRead) => {
  const end = getBookingDuration(booking) * 60 * 1000;
  const duration = intervalToDuration({ start: 0, end });
  return normalizeInHours(duration);
};

export const getBookingDurationOutput = (booking: ProviderBookingRead) => {
  const duration = getBookingTime(booking);
  return formDurationOutput(duration);
};

export const getBookingsDurationOutput = (
  totalHours: number,
  bookings: ProviderBookingRead[],
  extraTime?: string | undefined
) => {
  const end = totalHours
    ? Math.ceil(totalHours) * 60
    : bookings.reduce((acc, booking) => {
        if (booking.status_by_provider !== StatusByProviderEnum.REJECTED) {
          const value = getBookingDuration(booking);
          acc += value;
        }
        return acc;
      }, 0);
  const totalAmount =
    (extraTime && extraTime !== '00:00') || extraTime === '00:00:00'
      ? getMinutesFromTime(extraTime) + end
      : end;
  const duration = intervalToDuration({ start: 0, end: totalAmount * 60 * 1000 });
  const normalized = normalizeInHours(duration);
  return formDurationOutput(normalized);
};

export const substituteTimeByMask = (time: string) => {
  const subsequence = TimeMask.split('');
  subsequence.splice(0, time.length, ...time);
  return subsequence.join('');
};
